import { useState, useEffect, useCallback } from 'react'
import {
 Smartphone,  Globe,   CheckCircle2,   XCircle,   ShieldAlert,   LogIn,    User,    Eye,    MapPin,    Monitor,    Network, CalendarDays
} from 'lucide-react'
import { toast } from 'sonner'

// Componentes UI Globales
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { GlobalFilterBar } from '../components/ui/GlobalFilterBar'
import { Checkbox } from '../components/ui/checkbox'
import { Button } from '../components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select'
// Servicio
import { accessLogService, AccessLog } from '../services/access-log.service'

import { Check } from 'lucide-react'

// ─── Tipos ─────────────────────────────────────────────────────────────────────

interface PaginationState {
    currentPage: number
    total: number
    perPage: number
    lastPage: number
    from: number
    to: number
}

const INITIAL_PAGINATION: PaginationState = {
    currentPage: 1,
    total: 0,
    perPage: 10,
    lastPage: 1,
    from: 0,
    to: 0,
}

// ─── Configuración de tipo de login ──────────────────────────────────────────

interface LoginTypeConfig {
    label: string
    bgColor: string
    textColor: string
    Icon: React.ElementType
}

const LOGIN_TYPE_CONFIG: Record<string, LoginTypeConfig> = {
    //web: { label: 'Web',          bgColor: 'bg-primary/15',   textColor: 'text-primary',   Icon: Globe },
    pin: { label: 'PIN', bgColor: 'bg-violet-500/10', textColor: 'text-violet-500', Icon: Smartphone },
    password: { label: 'Contraseña', bgColor: 'bg-orange-500/10', textColor: 'text-orange-500', Icon: User },
    //api: { label: 'API',          bgColor: 'bg-secondary',  textColor: 'text-secondary-foreground',  Icon: Monitor },
}

const getLoginTypeCfg = (type: string): LoginTypeConfig =>
    LOGIN_TYPE_CONFIG[type?.toLowerCase()] ?? {
        label: type, bgColor: 'bg-secondary', textColor: 'text-secondary-foreground', Icon: Globe,
    }

// ─── Colores de rol ───────────────────────────────────────────────────────────

const ROLE_PALETTE = [
    'bg-destructive/10 text-destructive',
    'bg-primary/15 text-primary',
    'bg-emerald-500/10 text-emerald-500',
    'bg-purple-500/10 text-purple-500',
    'bg-orange-500/10 text-orange-500',
    'bg-cyan-500/10 text-cyan-500',
    'bg-amber-500/10 text-amber-500',
    'bg-secondary text-secondary-foreground',
]

function getRoleColor(roleName: string | null): string {
    if (!roleName) return 'bg-secondary text-secondary-foreground'
    const n = roleName.toLowerCase()
    if (n.includes('admin')) return ROLE_PALETTE[0]
    if (n.includes('encargado') || n.includes('manager')) return ROLE_PALETTE[1]
    if (n.includes('vendedor') || n.includes('seller')) return ROLE_PALETTE[2]
    if (n.includes('farmacia')) return ROLE_PALETTE[3]

    let hash = 0
    for (let i = 0; i < roleName.length; i++) {
        hash = roleName.charCodeAt(i) + ((hash << 5) - hash)
    }
    return ROLE_PALETTE[Math.abs(hash) % ROLE_PALETTE.length]
}

// ─── Formatear fecha ──────────────────────────────────────────────────────────

function formatDate(raw: string): string {
    try {
        const d = new Date(raw)
        const now = new Date()
        const isToday = d.toDateString() === now.toDateString()
        const yesterday = new Date(now)
        yesterday.setDate(now.getDate() - 1)
        const isYesterday = d.toDateString() === yesterday.toDateString()

        const time = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })

        if (isToday) return `Hoy · ${time}`
        if (isYesterday) return `Ayer · ${time}`

        return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) + ` · ${time}`
    } catch {
        return raw
    }
}

// ─── Componente Principal ─────────────────────────────────────────────────────

export function LogsAccesos() {
    // ─── Estado ───
    const [logs, setLogs] = useState<AccessLog[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [selectedLog, setSelectedLog] = useState<AccessLog | null>(null)
    const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION)

    // Filtros
    const [searchQuery, setSearchQuery] = useState('')
    const [filterLoginType, setFilterLoginType] = useState('')
    const [filterSuccess, setFilterSuccess] = useState<string>('')  // '' | 'true' | 'false'
    const [showAllSucursals, setShowAllSucursals] = useState(false)

    // ─── Fetch desde backend ───
    const fetchLogs = useCallback(async (page = 1) => {
        setIsLoading(true)
        try {
            const params: Parameters<typeof accessLogService.getLogs>[0] = {
                page,
                per_page: 10,
                search: searchQuery || undefined,
                login_type: filterLoginType || undefined,
                show_all_sucursals: showAllSucursals,
            }

            if (filterSuccess === 'true') params.success = true
            if (filterSuccess === 'false') params.success = false

            const res = await accessLogService.getLogs(params)

            const paginator = res?.data

            if (paginator?.data) {
                setLogs(paginator.data)
                setPagination({
                    currentPage: paginator.current_page,
                    total: paginator.total,
                    perPage: paginator.per_page,
                    lastPage: paginator.last_page,
                    from: paginator.from ?? 0,
                    to: paginator.to ?? 0,
                })
            } else {
                setLogs([])
                setPagination(INITIAL_PAGINATION)
            }
        } catch {
            toast.error('Error al cargar los registros de acceso')
        } finally {
            setIsLoading(false)
        }
    }, [searchQuery, filterLoginType, filterSuccess, showAllSucursals])

    useEffect(() => {
        fetchLogs(1)
    }, [fetchLogs])

    const handlePageChange = (page: number) => {
        fetchLogs(page)
    }

    // ─── Renderizado ──────────────────────────────────────────────────────────
    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Registro de Accesos</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Historial de inicios y cierres de sesión de los usuarios del sistema.
                    </p>
                </div>

            </div>

            <GlobalFilterBar
                show={['search', 'loginType', 'successResult', 'allBranches']}
                values={{ 
                    search: searchQuery,
                    loginType: filterLoginType,
                    successResult: filterSuccess,
                    allBranches: showAllSucursals
                }}
                onChange={(changes) => {
                    if (changes.search !== undefined) setSearchQuery(changes.search)
                    if (changes.loginType !== undefined) setFilterLoginType(changes.loginType)
                    if (changes.successResult !== undefined) setFilterSuccess(changes.successResult)
                    if (changes.allBranches !== undefined) setShowAllSucursals(changes.allBranches)
                }}
                customOptions={{ searchPlaceholder: 'Buscar usuario, sucursal...' }}
            />
            {/* Alerta de accesos fallidos */}
            {!isLoading && logs.some(l => l.success === false) && (
                <div className="flex items-center gap-3 rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                    <ShieldAlert className="h-4 w-4 shrink-0 text-destructive" />
                    <span>
                        Se detectaron <strong>{logs.filter(l => l.success === false).length}</strong> intentos de acceso fallidos en esta página.
                    </span>
                </div>
            )}

            {/* Tabla */}
            {isLoading ? (
               <TableSkeleton headers={['Usuario', 'Rol', 'Sucursal', 'Dispositivo', 'Tipo', 'Resultado', 'Fecha', 'Acciones',
                ]}
                columns={['text','badge', 'text', 'text', 'badge', 'badge', 'text', 'text', ]}    rows={8}
            />
            ) : (
                <div className="overflow-hidden rounded-xl border border-border bg-table-bg shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-nowrap">
                            <thead className="border-b border-border bg-table-header-bg">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Usuario</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Rol</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Sucursal</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Dispositivo</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Tipo</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Resultado</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Fecha</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border/50">
                                {logs.length > 0 ? (
                                    logs.map((log, index) => {
                                        const loginCfg = getLoginTypeCfg(log.login_type)
                                        const isSuccess = log.success !== false  // true o undefined → exitoso

                                        return (
                                            <tr
                                                key={index}
                                                className={`transition-colors hover:bg-table-row-hover ${!isSuccess ? 'bg-destructive/5' : ''}`}
                                            >
                                                {/* Usuario */}
                                                <td className="px-4 py-3">
                                                    <p className="font-medium text-table-foreground">{log.user_name || 'Correo no registrado'}</p>
                                                    <p className="text-xs text-muted-foreground">{log.user_email}</p>
                                                </td>

                                                {/* Rol */}
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getRoleColor(log.user_role)}`}>
                                                        {log.user_role || 'Sin rol'}
                                                    </span>
                                                </td>

                                                {/* Sucursal */}
                                                <td className="px-4 py-3 text-sm text-table-foreground">
                                                    {log.sucursal_name || <span className="text-muted-foreground/50">—</span>}
                                                </td>  

                                                {/* Dispositivo */}
                                                <td className="px-4 py-3 text-sm text-table-foreground">
                                                    {log.device_name || 'Navegador web'}
                                                </td>

                                                {/* Tipo de acceso */}
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${loginCfg.bgColor} ${loginCfg.textColor}`}>
                                                        <loginCfg.Icon className="h-3 w-3" />
                                                        {loginCfg.label}
                                                    </span>
                                                </td>

                                                {/* Resultado — NUEVA columna clave */}
                                                <td className="px-4 py-3">
                                                    {isSuccess ? (
                                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-500">
                                                            <CheckCircle2 className="h-3 w-3" />
                                                            Exitoso
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive">
                                                            <XCircle className="h-3 w-3" />
                                                            Fallido
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Fecha */}
                                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                                    {formatDate(log.logged_in_at)}
                                                </td>

                                                {/* Acciones */}
                                                <td className="px-4 py-3 text-center">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => setSelectedLog(log)}
                                                        title="Ver detalles"
                                                        className="h-8 w-8"
                                                    >
                                                       <Eye className="h-4 w-4 text-white" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        )
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={8} className="px-4 py-16 text-center">
                                            <LogIn className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
                                            <p className="text-sm font-medium text-muted-foreground">No se encontraron registros</p>
                                            <p className="mt-1 text-xs text-muted-foreground/70">Intenta ajustar los filtros de búsqueda</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginación */}
                    {pagination.total > 0 && (
                        <GlobalPaginator
                            totalItems={pagination.total}
                            itemsPerPage={pagination.perPage}
                            currentPage={pagination.currentPage}
                            from={pagination.from}
                            to={pagination.to}
                            onPageChange={handlePageChange}
                        />
                    )}
                </div>
            )}
            <Dialog
                    open={selectedLog !== null}
                    onOpenChange={(open) => {
                        if (!open) setSelectedLog(null)
                    }}
                >
                    <DialogContent className="sm:max-w-[680px] bg-card text-card-foreground">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <LogIn className="h-5 w-5 text-primary" />
                                Detalles del inicio de sesión
                            </DialogTitle>

                            <DialogDescription>
                                Información registrada durante el intento de acceso al sistema.
                            </DialogDescription>
                        </DialogHeader>

                        {selectedLog && (
                            <div className="space-y-5 py-2">

                                {/* Estado */}
                                <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
                                    <div>
                                        <p className="font-medium-headers-foreground">
                                            {selectedLog.user_name || 'Usuario desconocido'}
                                        </p>

                                        <p className="text-sm text-card-foreground/60">
                                            {selectedLog.user_email || 'Sin correo registrado'}
                                        </p>
                                    </div>

                                    {selectedLog.success ? (
                                        <Badge className="gap-1.5 bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/10">
                                            <CheckCircle2 className="h-3.5 w-3.5" />
                                            Exitoso
                                        </Badge>
                                    ) : (
                                        <Badge className="gap-1.5 bg-destructive/20 text-destructive hover:bg-destructive/10">
                                            <XCircle className="h-3.5 w-3.5" />
                                            Fallido
                                        </Badge>
                                    )}
                                </div>

                                {/* Información general */}
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                                    <div className="rounded-lg border border-border p-4">
                                        <div className="mb-1 flex items-center gap-2 text-xs text-headers-foreground">
                                            <User className="h-3.5 w-3.5" />
                                            Rol
                                        </div>

                                        <p className="text-sm font-medium text-card-foreground/60">
                                            {selectedLog.user_role || 'Sin rol'}
                                        </p>
                                    </div>

                                    <div className="rounded-lg border border-border p-4">
                                        <div className="mb-1 flex items-center gap-2 text-xs text-headers-foreground">
                                            <MapPin className="h-3.5 w-3.5" />
                                            Sucursal
                                        </div>

                                        <p className="text-sm font-medium text-card-foreground/60">
                                            {selectedLog.sucursal_name || '—'}
                                        </p>
                                    </div>

                                    <div className="rounded-lg border border-border p-4">
                                        <div className="mb-1 flex items-center gap-2 text-xs text-headers-foreground">
                                            <Network className="h-3.5 w-3.5" />
                                            Dirección IP
                                        </div>

                                        <p className="text-sm font-medium text-card-foreground/60">
                                            {selectedLog.ip_address || '—'}
                                        </p>
                                    </div>

                                    <div className="rounded-lg border border-border p-4">
                                        <div className="mb-1 flex items-center gap-2 text-xs text-headers-foreground">
                                            <Monitor className="h-3.5 w-3.5" />
                                            Dispositivo
                                        </div>

                                        <p className="text-sm font-medium text-card-foreground/60">
                                            {selectedLog.device_name || 'Navegador web'}
                                        </p>
                                    </div>

                                    <div className="rounded-lg border border-border p-4">
                                        <div className="mb-1 flex items-center gap-2 text-xs text-headers-foreground">
                                            <LogIn className="h-3.5 w-3.5" />
                                            Tipo de acceso
                                        </div>

                                        <p className="text-sm font-medium text-card-foreground/60">
                                            {getLoginTypeCfg(selectedLog.login_type).label}
                                        </p>
                                    </div>

                                    <div className="rounded-lg border border-border p-4">
                                        <div className="mb-1 flex items-center gap-2 text-xs text-headers-foreground">
                                            <CalendarDays className="h-3.5 w-3.5" />
                                            Fecha y hora
                                        </div>

                                        <p className="text-sm font-medium text-card-foreground/60">
                                            {formatDate(selectedLog.logged_in_at)}
                                        </p>
                                    </div>

                                </div>

                                {/* User Agent */}
                                <div>
                                    <div className="mb-2 flex items-center gap-2">
                                        <Globe className="h-4 w-4 text-headers-foreground" />

                                        <p className="text-sm font-medium text-headers-foreground">
                                            Navegador / User Agent
                                        </p>
                                    </div>

                                    <div className="rounded-lg border border-border bg-muted/30 p-4">
                                        <p className="break-all font-mono text-xs leading-relaxed text-headers-foreground">
                                            {selectedLog.user_agent || 'No disponible'}
                                        </p>
                                    </div>
                                </div>

                            </div>
                        )}
                    </DialogContent>
                </Dialog>
        </div>
    )
}
